import * as React from 'react';
import { debounce } from '@core-front/helper';
import { useOverflowItems } from './use-overflow-items';
const TabsNavCore = (props) => {
    const { Dropdown, TabsNavItem } = props.components;
    const innerElement = React.useRef(null);
    const activeElement = React.useRef(null);
    const underline = React.useRef();
    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
    const [focusedElementIndex, setFocusedElementIndex] = React.useState(-1);
    const [animatingUnderline, setAnimatingUnderline] = React.useState(false);
    const timeout = 0;
    let activeItem = null;
    const { items, overflowItems } = useOverflowItems({
        element: innerElement,
        disabled: !!props.disableDropdown,
        items: props.items,
    });
    const visibleItemsCount = items.length - overflowItems.length;
    const { widthFormatter, leftFormatter, } = props;
    const BEM = () => {
        const classArray = ['tabs__nav'];
        if (props.className) {
            classArray.push(props.className);
        }
        return classArray.join(' ');
    };
    const underlineBEM = () => {
        const classArray = ['tabs__nav-underline'];
        if (underline.current && underline.current.type) {
            classArray.push('tabs__nav-underline--type-' + underline.current.type);
        }
        if (animatingUnderline) {
            classArray.push('is-animating');
        }
        return classArray.join(' ');
    };
    const getUnderlineStyle = () => {
        return {
            maxWidth: (underline.current && !Number.isNaN(underline.current.width)) ? underline.current.width : 0,
            left: underline.current && underline.current.left,
        };
    };
    const updateUnderline = React.useCallback((item, isResizing = false) => {
        var _a;
        if (activeElement.current) {
            const style = getComputedStyle(activeElement.current);
            const newUnderlineProps = {
                width: widthFormatter ? widthFormatter(activeElement) : parseFloat(style.width) - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight),
                left: leftFormatter ? leftFormatter(activeElement, innerElement) : activeElement.current.offsetLeft,
                type: item ? item.type : (_a = underline.current) === null || _a === void 0 ? void 0 : _a.type,
            };
            if (underline.current !== newUnderlineProps) {
                underline.current = newUnderlineProps;
                if (!isResizing) {
                    setAnimatingUnderline(true);
                    window.clearTimeout(timeout);
                    window.setTimeout(() => {
                        setAnimatingUnderline(false);
                    }, 300);
                }
            }
        }
    }, [widthFormatter, leftFormatter]);
    const setActiveElement = (element, item) => {
        if (element && activeElement.current !== element && item.current) {
            activeElement.current = element;
            activeItem = item;
            if (innerElement.current) {
                updateUnderline(activeItem);
            }
        }
    };
    const resizeHandler = (isResizing) => {
        updateUnderline(undefined, isResizing);
    };
    const debouncedResizeHandler = debounce(resizeHandler.bind(this, true), 100);
    React.useEffect(() => {
        window.addEventListener('resize', debouncedResizeHandler);
        return () => {
            window.removeEventListener('resize', debouncedResizeHandler);
            debouncedResizeHandler.clear();
        };
    });
    const handleClick = (item, event) => {
        event.preventDefault();
        setIsDropdownOpen(false);
        if (item.onClick) {
            item.onClick(event);
        }
    };
    const handleKeyUp = (event) => {
        event.preventDefault();
        event.stopPropagation();
        let tabs = [];
        if (innerElement.current instanceof Element) {
            tabs = Array.from(innerElement.current.querySelectorAll('[role="tab"]'));
        }
        const focusPosition = document.activeElement ? tabs.indexOf(document.activeElement) : 0;
        switch (event.key) {
            case 'ArrowLeft':
            case 'ArrowUp':
                if (focusPosition > 0) {
                    focusTab(focusPosition - 1, tabs);
                }
                else {
                    focusTab(tabs.length - 1, tabs);
                }
                break;
            case 'ArrowRight':
            case 'ArrowDown':
                if (focusPosition < tabs.length - 1) {
                    focusTab(focusPosition + 1, tabs);
                }
                else {
                    focusTab(0, tabs);
                }
                break;
            case 'Home':
                focusTab(0, tabs);
                break;
            case 'End':
                focusTab(tabs.length - 1, tabs);
                break;
            case ' ':
            case 'Enter': {
                if (event.target instanceof HTMLElement) {
                    const tab = event.target;
                    tab.click();
                    setFocusedElementIndex(tabs.indexOf(tab));
                }
                break;
            }
        }
    };
    const focusTab = (index, tabs) => {
        const element = tabs[index];
        element.focus();
        setFocusedElementIndex(index);
    };
    const handleKeyDown = (event) => {
        switch (event.key) {
            case 'Tab':
                if (isDropdownOpen) {
                    setIsDropdownOpen(false);
                }
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
            case 'ArrowRight':
            case 'ArrowDown':
                // prevent document scroll when navigating with arrow keys
                event.preventDefault();
                break;
        }
        if (props.onKeyDown) {
            props.onKeyDown(event);
        }
    };
    React.useEffect(() => {
        if (focusedElementIndex >= visibleItemsCount) {
            setIsDropdownOpen(true);
        }
        else {
            setIsDropdownOpen(false);
        }
    }, [props.disableDropdown, focusedElementIndex, visibleItemsCount]);
    React.useEffect(() => {
        var _a;
        if ((_a = props.elementRef) === null || _a === void 0 ? void 0 : _a.current) {
            innerElement.current = props.elementRef.current;
        }
    }, [props.elementRef]);
    React.useEffect(() => {
        if (activeElement.current && innerElement.current && activeItem) {
            updateUnderline(activeItem);
        }
    }, [activeElement, innerElement, activeItem, updateUnderline]);
    return (React.createElement("div", { className: BEM(), onKeyUp: handleKeyUp, onKeyDown: handleKeyDown },
        underline && visibleItemsCount - 1 >= focusedElementIndex && (React.createElement("span", { className: underlineBEM(), style: getUnderlineStyle() })),
        !!items.length && (React.createElement("ul", { className: "tabs__nav-inner", ref: innerElement, role: "tablist", "aria-labelledby": props['aria-labelledby'] }, items.map((item, index) => (React.createElement(TabsNavItem, Object.assign({}, item, { onClick: (event) => handleClick(item, event), key: item.id || index, tabIndex: item.current ? 0 : -1, onlyChild: index === 0 && visibleItemsCount === 1, "aria-selected": item.current, className: index > (visibleItemsCount - 1) ? 'sr-only' : undefined, elementRef: (element) => setActiveElement(element, item) })))))),
        !props.disableDropdown && overflowItems.length > 0 && (React.createElement(Dropdown, { id: `${props['aria-labelledby']}-dropdown`, button: {
                icon: '#more-vertical',
                // empty string is OK here because the element is hidden from screen readers with aria-hidden
                text: '',
                size: 'small',
                variant: 'secondary',
                className: 'tabs__nav-dropdown-button',
                attributes: {
                    'tabIndex': -1,
                    'aria-hidden': true,
                },
            }, isOpen: isDropdownOpen, onToggle: setIsDropdownOpen, width: "auto", align: "end", items: overflowItems.map((item, index) => {
                return Object.assign(Object.assign({}, item), { label: item.text, url: item.url, onClick: (event) => handleClick(item, event), isActive: focusedElementIndex === (visibleItemsCount + index), icon: item.icon, tabIndex: -1, id: `${item.id}-dropdown`, color: item.type, elementRef: (element) => setActiveElement(element, item) });
            }) })),
        props.children));
};
export { TabsNavCore as default, };
